import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Loader2, ExternalLink, X, AlertTriangle, ChevronDown, Paperclip } from 'lucide-react';
import { api } from '../lib/api';
import {
  sendPreparedSigning, discardPreparedSigning, getSigningStatus, fetchFileBlob, type PreparedSigning,
} from '../lib/vorgangApi';
import { EditableMailPreview } from './EditableMailPreview';
import { type SigningPreview } from './SigningPreviewDialog';

interface EmailPreviewResp {
  defaultSubject: string; defaultMessage: string; defaultAnrede: string; defaultClosing: string; defaultKontoauszug?: string; subject: string; html: string;
}
export interface SigningEmailReviewProps {
  open: boolean;
  reportId?: number;
  /** Kunden-ebener Dauer-Dokument-Versand (ohne Bericht): Vorschau kommt vom Kunden-Endpunkt. */
  dauerCustomerId?: number;
  email: string;
  /** Vorbelegung für das CC-Feld (i.d.R. die eigene E-Mail des Beraters). */
  defaultCc?: string;
  context: { bitteKontoauszug?: boolean };
  /** Bericht-Flow: Welche Dokumente im Paket stecken — als aufklappbarer Bereich im Dialog.
   *  Fehlt die Angabe (VN, Dauer-Dokumente ohne Auswahl), gibt es nichts auszuwählen. */
  docAuswahl?: { preview: SigningPreview | null; loading: boolean };
  /** Vorbelegung der Abwahl beim Öffnen — z.B. im Dauer-Flow: nur den Typ ankreuzen, dessen
   *  Menü man geöffnet hat, alle anderen vorab abwählen. Ohne Angabe ist alles angehakt. */
  initialExcludedTypes?: string[];
  /** Erzeugt den ECHTEN (noch nicht gesendeten) Auftrag + Test-Link. Elternspezifisch
   *  (Bericht/VN/Dauer). Die Auswahl bestimmt das Paket — ändert sie sich, wird neu
   *  vorbereitet, damit der Test-Link genau das zeigt, was der Kunde bekommt. */
  prepare: (email: string, auswahl: { forceTypes: string[]; excludeTypes: string[]; bitteKontoauszug: boolean }) => Promise<PreparedSigning>;
  onCancel: () => void;
  /** Nach erfolgreichem Versand — Eltern schließt den Dialog + aktualisiert. */
  onSent: () => void;
  /** Optionale externe Meldung. */
  error?: string | null;
  flow?: 'bericht' | 'vn';
  /** Eigene Vorschau-Quelle statt der zwei fest verdrahteten Signatur-Endpunkte (Bericht/
   *  Dauer) — z.B. für "Daten anfordern". Läuft wie bisher PARALLEL zu prepare(), braucht
   *  also keinen Token. Ist dieser Prop gesetzt, werden reportId/dauerCustomerId ignoriert. */
  fetchPreviewFn?: (body: { subject?: string; message?: string; anrede?: string; closing?: string; kontoauszug?: string }) => Promise<EmailPreviewResp>;
  /** Eigene Versand-/Verwerfen-Funktion statt sendPreparedSigning/discardPreparedSigning
   *  (gleiche Signatur, anderer Endpunkt) — für Flows außerhalb der Signatur-Anfragen. */
  sendFn?: (token: string, body: { customerEmail?: string; subject?: string; message?: string; anrede?: string; closing?: string; kontoauszug?: string; cc?: string }) => Promise<{ ok: boolean }>;
  discardFn?: (token: string) => Promise<{ ok: boolean }>;
  /** "Kopie an"-Feld ausblenden, falls ein Aufrufer das mal nicht unterstützt. */
  hideCc?: boolean;
  /** Generische Zusatz-Option im Kopf des Dialogs (z.B. "Dauerdokumente mitschicken").
   *  Ändert sich `checked`, werden Vorschau und Entwurf neu gebaut — die Eltern-Closures
   *  `prepare`/`fetchPreviewFn` müssen den neuen Wert dann bereits einschließen. */
  extraToggle?: { label: string; checked: boolean; onChange: (checked: boolean) => void };
}

/** Stabile Referenz für "nichts abgewählt" — verhindert, dass useCallback bei jedem
 *  Render eine neue Vorschau anfordert. */
const EMPTY_TYPES: string[] = [];

export default function SigningEmailReview(props: SigningEmailReviewProps) {
  const { open, reportId, email, defaultCc, context, prepare, onCancel, onSent, error: extError } = props;
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [anrede, setAnrede] = useState('');
  const [closing, setClosing] = useState('');
  const [kontoauszugText, setKontoauszugText] = useState('');
  const [toEmail, setToEmail] = useState(email);
  const [cc, setCc] = useState('');
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lebenszyklus: vorbereiten (Test-Link) → senden / verwerfen.
  const [prepared, setPrepared] = useState<PreparedSigning | null>(null);
  const [ready, setReady] = useState(false);
  const [prepErr, setPrepErr] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);

  // Dokumentauswahl (nur Bericht-Flow). Sie bestimmt das Paket, deshalb muss bei jeder
  // Änderung der vorbereitete Auftrag neu gebaut werden — sonst zeigt der Test-Link ein
  // anderes Paket als der Kunde bekommt.
  const [excluded, setExcluded] = useState<Record<string, boolean>>({});
  const [bitteKontoauszug, setBitteKontoauszug] = useState<boolean | null>(null);
  const [auswahlOffen, setAuswahlOffen] = useState(false);
  const auswahlPreview = props.docAuswahl?.preview ?? null;
  const kontoauszugDa = auswahlPreview?.hasKontoauszug ?? false;
  const effektivBitte = bitteKontoauszug ?? (props.docAuswahl ? !kontoauszugDa : !!context.bitteKontoauszug);
  const auswahl = {
    // Nachfordern bereits unterschriebener Dauer-Dokumente läuft über die Kundenseite
    // („Dokumente zur Unterschrift senden" schickt ohnehin immer alle drei).
    forceTypes: [] as string[],
    excludeTypes: Object.entries(excluded).filter(([, an]) => an).map(([typ]) => typ),
    bitteKontoauszug: effektivBitte,
  };
  // Referenzstabil über den Inhalt: ein bei jedem Render neues Array macht fetchPreview neu
  // → Lade-Effekt feuert endlos → /…email-preview wird geflutet („Lädt…" bleibt stehen).
  const excludeKey = JSON.stringify(auswahl.excludeTypes.slice().sort());
  const auswahlExclude = useMemo<string[]>(() => (excludeKey === '[]' ? EMPTY_TYPES : JSON.parse(excludeKey)), [excludeKey]);
  const auswahlSchluessel = JSON.stringify([auswahl.excludeTypes.slice().sort(), auswahl.bitteKontoauszug]);
  // Kein Dokument mehr im Paket: erlaubt, solange wenigstens der Kontoauszug erbeten wird.
  const nurKontoauszug = Boolean(props.docAuswahl) && (auswahlPreview?.files ?? []).length > 0
    && (auswahlPreview?.files ?? []).every(f => excluded[f.type]);
  const nichtsAngefordert = nurKontoauszug && !effektivBitte;
  const auswahlRef = useRef(auswahl);
  useEffect(() => { auswahlRef.current = auswahl; });

  // Aktuelle Texte auch für refreshPreview verfügbar halten, ohne die Vorschau bei
  // jedem Tastendruck neu zu bauen (das würde im contentEditable den Cursor verlieren).
  const texteRef = useRef({ subject, message, anrede, closing, kontoauszug: kontoauszugText });
  useEffect(() => {
    texteRef.current = { subject, message, anrede, closing, kontoauszug: kontoauszugText };
  }, [subject, message, anrede, closing, kontoauszugText]);

  useEffect(() => {
    if (!open) return;
    setToEmail(email); setCc(defaultCc ?? '');
    setExcluded(Object.fromEntries((props.initialExcludedTypes ?? []).map(t => [t, true])));
    setBitteKontoauszug(null); setAuswahlOffen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, email, defaultCc]);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail.trim());

  const fetchPreview = useCallback(
    async (body: { subject?: string; message?: string; anrede?: string; closing?: string; kontoauszug?: string }) => {
      if (props.fetchPreviewFn) return props.fetchPreviewFn(body);
      if (props.dauerCustomerId != null) {
        // Auswahl (falls vorhanden) mitgeben, damit die Vorschau exakt das Paket beschreibt,
        // das gesendet wird — ohne docAuswahl (z.B. der volle "Zur Unterschrift senden"-Knopf)
        // bleibt types weg = wie bisher alle vorhandenen Entwürfe.
        // Solange die Paket-Vorschau noch lädt, trotzdem die Abwahl berücksichtigen — sonst
        // beschreibt die erste Vorschau-Mail alle drei Dauer-Dokumente.
        const types = props.docAuswahl
          ? (props.docAuswahl.preview?.files?.map(f => f.type) ?? ['vollmacht', 'grc', 'de_minimis']).filter(t => !excluded[t])
          : undefined;
        return api.post<EmailPreviewResp>(`/api/customers/${props.dauerCustomerId}/dauer-signing-email-preview`, { ...body, types });
      }
      return api.post<EmailPreviewResp>(
        `/api/reports/${reportId}/signing-email-preview`,
        {
          ...body,
          bitteKontoauszug: effektivBitte,
          flow: props.flow ?? 'bericht',
          // Auswahl mitgeben, damit die Vorschau exakt das Paket beschreibt, das gesendet wird.
          excludeTypes: auswahlExclude,
        },
      );
    },
    // props.docAuswahl.preview (nicht der ganze Wrapper!) — der Wrapper wird von manchen
    // Aufrufern bei jedem Render neu literal erzeugt (siehe EditorWorkspace 2026-09-22:
    // dadurch feuerte der Lade-Effekt unten bei JEDEM Eltern-Render neu → /signing-email-preview
    // wurde geflutet, "Zu viele Anfragen"). .preview ist die eigentlich gelesene, stabile Nutzlast.
    [reportId, effektivBitte, props.flow, props.dauerCustomerId, props.docAuswahl?.preview, excluded, auswahlExclude, props.fetchPreviewFn],
  );

  // E-Mail-Text laden.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true); setError(null);
    fetchPreview({})
      .then(r => { if (!alive) return; setSubject(r.defaultSubject); setMessage(r.defaultMessage); setAnrede(r.defaultAnrede); setClosing(r.defaultClosing); setKontoauszugText(r.defaultKontoauszug ?? ''); setHtml(r.html); })
      .catch((e: any) => { if (alive) setError(e?.message ?? 'Vorschau konnte nicht geladen werden'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, fetchPreview]);

  // Auftrag + Test-Link vorbereiten (kein Mailversand): beim Öffnen und nach jeder
  // Änderung der Dokumentauswahl. Der vorherige Entwurf wird dabei verworfen.
  const preparedRef = useRef<PreparedSigning | null>(null);
  useEffect(() => { preparedRef.current = prepared; }, [prepared]);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    const alter = preparedRef.current;
    if (alter) (props.discardFn ?? discardPreparedSigning)(alter.token).catch(() => { /* best-effort */ });
    setPrepared(null); setReady(false); setPrepErr(null); setActionErr(null);
    prepare(email, auswahlRef.current)
      .then(p => { if (!alive) return; setPrepared(p); setReady(p.ready); })
      .catch((e: any) => { if (alive) setActionErr(e?.message ?? 'Vorbereitung fehlgeschlagen'); });
    return () => { alive = false; };
    // Adressänderung wird beim Senden als Override übernommen — kein Neu-Vorbereiten nötig.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, auswahlSchluessel, props.extraToggle?.checked]);

  // VN: bis der Hintergrund-Scan die Unterschriftsseiten gesetzt hat, Status pollen.
  useEffect(() => {
    if (!open || !prepared || ready || prepErr) return;
    let alive = true;
    const id = setInterval(async () => {
      try {
        const s = await getSigningStatus(prepared.token);
        if (!alive) return;
        if (s.prepError) setPrepErr(s.prepError);
        else if (s.ready) setReady(true);
      } catch { /* weiter pollen */ }
    }, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [open, prepared, ready, prepErr]);

  const doCancel = useCallback(() => {
    if (prepared) (props.discardFn ?? discardPreparedSigning)(prepared.token).catch(() => { /* Entwurf-Aufräumen best-effort */ });
    onCancel();
  }, [prepared, onCancel, props.discardFn]);

  // Escape schließt den Dialog (verwirft den vorbereiteten Entwurf wie „Abbrechen").
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !sending) doCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, sending, doCancel]);

  /** Vorschau serverseitig neu bauen — nach dem Verlassen eines Feldes, nie beim Tippen. */
  const refreshPreview = () => {
    fetchPreview(texteRef.current).then(r => setHtml(r.html)).catch(() => { /* best-effort */ });
  };

  const onFeldChange = (feld: 'anrede' | 'message' | 'closing' | 'kontoauszug', text: string) => {
    if (feld === 'anrede') setAnrede(text);
    else if (feld === 'message') setMessage(text);
    else if (feld === 'kontoauszug') setKontoauszugText(text);
    else setClosing(text);
  };

  const openTestLink = () => { if (prepared && ready) window.open(prepared.signingUrl, '_blank'); };

  // Die Vorschau-Mail trägt einen Platzhalter-Link (…/unterschreiben/vorschau), der beim
  // Klicken „Link ungültig" zeigt. Sobald der Test-Link bereit ist, führt der Button dorthin.
  const previewHtml = prepared && ready
    ? html.replace(/https?:\/\/[^"'\s<>]*\/unterschreiben\/vorschau/g, prepared.signingUrl)
    : html;

  // Dokumente, die tatsächlich mitgehen (abgewählte fallen raus).
  const sichtbareDokumente = (auswahlPreview?.files ?? []).filter(f => !excluded[f.type]);

  // „Ansehen": Datei über den authentifizierten Weg holen und überlagert anzeigen.
  const [dok, setDok] = useState<{ url: string; name: string } | null>(null);
  const [dokLaedtId, setDokLaedtId] = useState<number | null>(null);
  const oeffneDokument = async (f: { id: number; name: string }) => {
    setDokLaedtId(f.id);
    try {
      const { url } = await fetchFileBlob(f.id);
      setDok({ url, name: f.name });
    } catch {
      /* Datei evtl. nicht verfügbar — Dialog bleibt offen */
    } finally {
      setDokLaedtId(null);
    }
  };
  const schliesseDokument = () => setDok(d => { if (d) URL.revokeObjectURL(d.url); return null; });

  const doSend = async () => {
    if (!prepared || !ready) return;
    setSending(true); setActionErr(null);
    try {
      await (props.sendFn ?? sendPreparedSigning)(prepared.token, {
        customerEmail: toEmail.trim(), subject, message, anrede, closing,
        // Nur mitschicken, wenn der Satz überhaupt in der Mail steht.
        ...(effektivBitte ? { kontoauszug: kontoauszugText } : {}),
        cc: cc.trim() || undefined,
      });
      onSent();
    } catch (e: any) {
      setActionErr(e?.message ?? 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;
  const meldung = extError || error || actionErr;
  const feldKlasse = 'flex-1 min-w-0 px-2 py-1 text-sm bg-transparent border-0 border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none text-slate-900 dark:text-white';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div role="dialog" aria-modal="true" aria-label="E-Mail prüfen"
        className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[92vh] flex flex-col overflow-hidden">

        {/* Kopf: Umschlag-Angaben, wie im Mailprogramm */}
        <div className="shrink-0 px-5 pt-4 pb-3 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="font-semibold text-slate-900 dark:text-white">E-Mail prüfen</h3>
            <button onClick={doCancel} disabled={sending} aria-label="Schließen"
              className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-baseline gap-2 border-b border-slate-100 dark:border-slate-700/60">
            <label htmlFor="ser-email" className="w-16 shrink-0 text-xs text-slate-500 dark:text-slate-400">An</label>
            <input id="ser-email" type="email" value={toEmail} onChange={e => setToEmail(e.target.value)}
              className={`${feldKlasse} ${toEmail.trim() && !emailValid ? 'border-red-400' : ''}`} />
          </div>
          {toEmail.trim() && !emailValid && <p className="text-xs text-red-600 mt-1 ml-16">Bitte eine gültige E-Mail-Adresse eingeben.</p>}

          {!props.hideCc && (
            <div className="flex items-baseline gap-2 border-b border-slate-100 dark:border-slate-700/60">
              <label htmlFor="ser-cc" className="w-16 shrink-0 text-xs text-slate-500 dark:text-slate-400">Kopie an</label>
              <input id="ser-cc" type="text" value={cc} onChange={e => setCc(e.target.value)}
                placeholder="optional" className={feldKlasse} />
            </div>
          )}

          <div className="flex items-baseline gap-2">
            <label htmlFor="ser-subject" className="w-16 shrink-0 text-xs text-slate-500 dark:text-slate-400">Betreff</label>
            <input id="ser-subject" value={subject} onChange={e => setSubject(e.target.value)} onBlur={refreshPreview}
              className={`${feldKlasse} font-medium`} />
          </div>

          {props.extraToggle && (
            <label className="flex items-start gap-2 mt-2.5 pt-2.5 border-t border-slate-100 dark:border-slate-700/60 cursor-pointer select-none">
              <input type="checkbox" checked={props.extraToggle.checked}
                onChange={e => props.extraToggle!.onChange(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-purple-600 focus:ring-purple-500" />
              <span className="text-xs text-slate-600 dark:text-slate-300">{props.extraToggle.label}</span>
            </label>
          )}
        </div>

        {/* Was im Paket steckt — aufgeklappt nur, wenn man etwas ändern will. */}
        {props.docAuswahl && (
          <div className="shrink-0 border-b border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setAuswahlOffen(o => !o)}
              aria-expanded={auswahlOffen}
              className="w-full flex items-center gap-2 px-5 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40"
            >
              <Paperclip className="w-3.5 h-3.5 shrink-0 text-slate-400" />
              <span className="text-sm text-slate-700 dark:text-slate-200">
                {props.docAuswahl.loading
                  ? 'Dokumente werden geladen…'
                  : sichtbareDokumente.length === 0
                    ? (effektivBitte ? 'Nur Kontoauszug angefordert' : 'Nichts ausgewählt')
                    : `${sichtbareDokumente.length} Dokument${sichtbareDokumente.length === 1 ? '' : 'e'} im Paket`}
              </span>
              <span className="flex-1 min-w-0 truncate text-xs text-slate-400">
                {!props.docAuswahl.loading && sichtbareDokumente.map(d => d.label).join(' · ')}
              </span>
              {!effektivBitte ? null : <span className="shrink-0 text-xs text-amber-700 dark:text-amber-400">+ Kontoauszug erbeten</span>}
              <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${auswahlOffen ? 'rotate-180' : ''}`} />
            </button>

            {auswahlOffen && (
              <div className="px-5 pb-3 space-y-3">
                {auswahlPreview?.files && auswahlPreview.files.length > 0 && (
                  <ul className="space-y-1.5">
                    {auswahlPreview.files.map(f => (
                      <li key={f.id} className="flex items-center justify-between gap-2 text-sm">
                        <label className="flex items-center gap-2 min-w-0 cursor-pointer">
                          <input type="checkbox" checked={!excluded[f.type]}
                            onChange={() => setExcluded(p => ({ ...p, [f.type]: !p[f.type] }))}
                            className="rounded border-slate-300 dark:border-slate-600 shrink-0" />
                          <span className={`truncate ${excluded[f.type] ? 'line-through text-slate-400' : 'text-slate-800 dark:text-slate-200'}`} title={f.name}>{f.label}</span>
                        </label>
                        <button onClick={() => oeffneDokument(f)} disabled={dokLaedtId === f.id}
                          className="shrink-0 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 disabled:opacity-50">
                          {dokLaedtId === f.id ? 'Lädt…' : 'Ansehen'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer border-t border-slate-100 dark:border-slate-700/60 pt-2.5">
                  <input type="checkbox" checked={effektivBitte}
                    onChange={e => setBitteKontoauszug(e.target.checked)}
                    className="mt-0.5 rounded border-slate-300 dark:border-slate-600" />
                  <span>
                    Kontoauszug mit erbitten
                    <span className="block text-xs text-slate-500 dark:text-slate-400">
                      {kontoauszugDa ? 'Liegt im Vorgang schon vor — der Kunde hat aber vielleicht einen neueren.' : 'Im Vorgang liegt noch keiner.'}
                    </span>
                  </span>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Die Mail selbst — Anrede, Nachricht und Grußformel sind direkt beschreibbar. */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 bg-slate-50 dark:bg-slate-900/40">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
            So sieht die E-Mail beim Kunden aus. Die <span className="px-1 py-0.5 rounded border border-dashed border-slate-400">gestrichelt umrandeten</span>{' '}
            Stellen — Anrede, Nachricht{effektivBitte ? ', Hinweis zum Kontoauszug' : ''} und Grußformel — können Sie direkt anklicken und ändern.
          </p>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white p-3">
            {loading
              ? <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</div>
              : <EditableMailPreview html={previewHtml} onChange={onFeldChange} onCommit={refreshPreview} />}
          </div>
        </div>

        {/* Fußleiste bleibt stehen: Test-Link links, Versand rechts. */}
        <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 px-5 py-3">
          {meldung && (
            <p className="flex items-start gap-1.5 text-sm text-red-600 mb-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{meldung}
            </p>
          )}
          {prepErr && <p className="text-xs text-red-600 mb-2">{prepErr} — bitte den Verwendungsnachweis prüfen.</p>}
          <div className="flex items-center justify-between gap-3">
            <button onClick={openTestLink} disabled={!ready}
              title="Öffnet die Signaturseite wie beim Kunden — ohne Versand"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed">
              {ready ? <ExternalLink className="w-3.5 h-3.5" /> : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {ready ? 'Test-Link öffnen' : prepErr ? 'Vorbereitung fehlgeschlagen' : 'wird vorbereitet…'}
            </button>
            <div className="flex items-center gap-2">
              <button onClick={doCancel} disabled={sending}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-50">Abbrechen</button>
              <button onClick={doSend} disabled={sending || !emailValid || !ready || nichtsAngefordert}
                title={nichtsAngefordert ? 'Bitte mindestens ein Dokument auswählen oder den Kontoauszug erbitten' : !ready ? 'Wird noch vorbereitet…' : !emailValid ? 'Bitte eine gültige E-Mail-Adresse eingeben' : undefined}
                className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg">
                {sending ? 'Sende…' : 'An Kunden senden'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {dok && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-black/70 p-4" onClick={schliesseDokument}>
          <div className="flex items-center justify-between gap-3 mb-2 text-white shrink-0" onClick={e => e.stopPropagation()}>
            <span className="text-sm font-medium truncate" title={dok.name}>{dok.name}</span>
            <button onClick={schliesseDokument} className="px-3 py-1.5 text-sm rounded-lg bg-white/15 hover:bg-white/25">Schließen</button>
          </div>
          <div className="flex-1 min-h-0" onClick={e => e.stopPropagation()}>
            <iframe src={dok.url} title={dok.name} className="w-full h-full rounded-lg bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}
